/**
 * Derived, non-serialised data built from a MissionDef and the set of surviving props:
 * colliders, the 1 m navigation grid, trench queries, line of sight, and the helpers the
 * systems share. Rebuilt whenever a prop is destroyed (cheap at this scale).
 */

import type { NavGrid } from './astar';
import { applyHazardNav, closedCrossings } from './hazards/terrain';
import { BULLET_BLOCK_HEIGHT, type Collider, collidersFor } from './props';
import { inPond, inTrench } from './terrainQueries';
import { RANGER } from './tuning';
import type { MissionDef, PropState } from './types';

/**
 * Something that blocks the ground without being a prop (ROUND4 §1): a burnt-out lorry across the road,
 * the rubble of a fallen span in the ford. `id` is only for telling them apart; they take collider ids
 * below -1000, which no prop and no `ignoreProp` sentinel ever uses.
 */
export interface Wreck {
  id: number;
  x: number;
  z: number;
  shape: 'circle' | 'box';
  r?: number;
  hx?: number;
  hz?: number;
  h: number;
}

export interface Statics {
  def: MissionDef;
  colliders: Collider[];
  nav: NavGrid;
  /** Bumped on every rebuild so cached paths can be invalidated. */
  version: number;
}

export function buildStatics(
  def: MissionDef,
  props: readonly PropState[],
  version = 0,
  wrecks: readonly Wreck[] = [],
): Statics {
  const alive = new Set(props.filter((p) => !p.destroyed).map((p) => p.id));
  const colliders: Collider[] = [];
  for (const prop of def.props) {
    if (alive.has(prop.id)) colliders.push(...collidersFor(prop));
  }
  for (const wreck of wrecks) {
    colliders.push({
      propId: -1000 - wreck.id,
      shape: wreck.shape,
      x: wreck.x,
      z: wreck.z,
      r: wreck.r ?? 0,
      hx: wreck.hx ?? 0,
      hz: wreck.hz ?? 0,
      h: wreck.h,
      blocksMove: true,
      blocksSight: true,
      blocksBullets: wreck.h >= BULLET_BLOCK_HEIGHT,
    });
  }

  const width = Math.ceil(def.sizeX);
  const height = Math.ceil(def.sizeZ);
  const blocked = new Uint8Array(width * height);
  const cost = new Float32Array(width * height).fill(1);
  const pad = RANGER.radius + 0.15;
  for (const c of colliders) {
    if (!c.blocksMove) continue;
    const ex = (c.shape === 'circle' ? c.r : c.hx) + pad;
    const ez = (c.shape === 'circle' ? c.r : c.hz) + pad;
    const x0 = Math.max(0, Math.floor(c.x - ex));
    const x1 = Math.min(width - 1, Math.floor(c.x + ex));
    const z0 = Math.max(0, Math.floor(c.z - ez));
    const z1 = Math.min(height - 1, Math.floor(c.z + ez));
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) {
        const px = x + 0.5;
        const pz = z + 0.5;
        const inside = c.shape === 'circle'
          ? (px - c.x) * (px - c.x) + (pz - c.z) * (pz - c.z) <= (c.r + pad) * (c.r + pad)
          : Math.abs(px - c.x) <= ex && Math.abs(pz - c.z) <= ez;
        if (inside) blocked[z * width + x] = 1;
      }
    }
  }
  // Water is impassable for now; trenches are passable but soldiers prefer not to use them.
  for (let z = 0; z < height; z++) {
    for (let x = 0; x < width; x++) {
      const px = x + 0.5;
      const pz = z + 0.5;
      if (inPond(def, px, pz)) blocked[z * width + x] = 1;
      else if (inTrench(def, px, pz)) cost[z * width + x] = 4;
    }
  }
  // HAZ: open water blocks soldiers except at fords; they route around their own mines and mini-bunkers.
  // R4: a ford under a fallen deck is no longer a ford - for the garrison's navigation as for the ranger's feet.
  applyHazardNav(def, width, height, blocked, cost, closedCrossings(def, props));
  return { def, colliders, nav: { width, height, blocked, cost }, version };
}

/* Terrain queries (trenches, roads, ponds, water) live in ./terrainQueries.ts; re-exported for every caller. */
export { inPond, inTrench, roadDistance, pondDepthAt, trenchDepthAt, waterAt } from './terrainQueries';
/* HAZ: water barriers (mission 6's river). The geometry lives in hazards/terrain.ts. */
export { iceAt, weakIceAt } from './hazards/terrain';

/* ---------------------------------------------------------------- segments and LOS */

/** Entry and exit parameters (0..1) of segment a->b through a collider's footprint, or null. */
export function segmentHit(
  c: Collider,
  ax: number,
  az: number,
  bx: number,
  bz: number,
  grow = 0,
): { enter: number; exit: number } | null {
  const dx = bx - ax;
  const dz = bz - az;
  if (c.shape === 'circle') {
    const r = c.r + grow;
    const fx = ax - c.x;
    const fz = az - c.z;
    const a = dx * dx + dz * dz;
    if (a === 0) return fx * fx + fz * fz <= r * r ? { enter: 0, exit: 0 } : null;
    const b = 2 * (fx * dx + fz * dz);
    const cc = fx * fx + fz * fz - r * r;
    const disc = b * b - 4 * a * cc;
    if (disc < 0) return null;
    const s = Math.sqrt(disc);
    const t0 = (-b - s) / (2 * a);
    const t1 = (-b + s) / (2 * a);
    if (t1 < 0 || t0 > 1) return null;
    return { enter: Math.max(0, t0), exit: Math.min(1, t1) };
  }
  let t0 = 0;
  let t1 = 1;
  const axes: Array<[number, number, number, number]> = [
    [ax, dx, c.x - c.hx - grow, c.x + c.hx + grow],
    [az, dz, c.z - c.hz - grow, c.z + c.hz + grow],
  ];
  for (const [o, d, lo, hi] of axes) {
    if (d === 0) {
      if (o < lo || o > hi) return null;
      continue;
    }
    let ta = (lo - o) / d;
    let tb = (hi - o) / d;
    if (ta > tb) {
      const tmp = ta;
      ta = tb;
      tb = tmp;
    }
    t0 = Math.max(t0, ta);
    t1 = Math.min(t1, tb);
    if (t0 > t1) return null;
  }
  return { enter: t0, exit: t1 };
}

/**
 * Line of sight between two points at given heights. A collider blocks the ray if the ray
 * is below the collider's top anywhere over its footprint - so a rock hides a crawler
 * right behind it but not a standing man, and nothing hides you from a watchtower.
 */
export function lineOfSight(
  s: Statics,
  ax: number,
  az: number,
  ah: number,
  bx: number,
  bz: number,
  bh: number,
  ignoreProp = -1,
): boolean {
  for (const c of s.colliders) {
    if (!c.blocksSight || c.propId === ignoreProp) continue;
    const hit = segmentHit(c, ax, az, bx, bz);
    if (!hit) continue;
    const hEnter = ah + (bh - ah) * hit.enter;
    const hExit = ah + (bh - ah) * hit.exit;
    if (Math.min(hEnter, hExit) < c.h) return false;
  }
  return true;
}

/** First bullet-stopping collider along a->b: parameter 0..1 and the collider, or null. */
export function firstBulletHit(
  s: Statics,
  ax: number,
  az: number,
  bx: number,
  bz: number,
  ignoreProp = -1,
): { t: number; collider: Collider } | null {
  let best: { t: number; collider: Collider } | null = null;
  for (const c of s.colliders) {
    if (!c.blocksBullets || c.propId === ignoreProp) continue;
    const hit = segmentHit(c, ax, az, bx, bz);
    if (hit && (!best || hit.enter < best.t)) best = { t: hit.enter, collider: c };
  }
  return best;
}

/** Push a circle of radius `r` out of every movement-blocking collider and back inside the map. */
export function resolveMovement(s: Statics, x: number, z: number, r: number): { x: number; z: number } {
  let px = x;
  let pz = z;
  for (let pass = 0; pass < 3; pass++) {
    for (const c of s.colliders) {
      if (!c.blocksMove) continue;
      if (c.shape === 'circle') {
        const dx = px - c.x;
        const dz = pz - c.z;
        const min = c.r + r;
        const d2 = dx * dx + dz * dz;
        if (d2 < min * min) {
          const d = Math.sqrt(d2);
          if (d < 1e-6) px += min;
          else {
            px = c.x + (dx / d) * min;
            pz = c.z + (dz / d) * min;
          }
        }
      } else {
        const nx = Math.min(c.x + c.hx, Math.max(c.x - c.hx, px));
        const nz = Math.min(c.z + c.hz, Math.max(c.z - c.hz, pz));
        const dx = px - nx;
        const dz = pz - nz;
        const d2 = dx * dx + dz * dz;
        if (d2 < r * r) {
          if (d2 > 1e-10) {
            const d = Math.sqrt(d2);
            px = nx + (dx / d) * r;
            pz = nz + (dz / d) * r;
          } else {
            // Centre is inside the box: leave by the nearest face.
            const left = px - (c.x - c.hx);
            const right = c.x + c.hx - px;
            const up = pz - (c.z - c.hz);
            const down = c.z + c.hz - pz;
            const m = Math.min(left, right, up, down);
            if (m === left) px = c.x - c.hx - r;
            else if (m === right) px = c.x + c.hx + r;
            else if (m === up) pz = c.z - c.hz - r;
            else pz = c.z + c.hz + r;
          }
        }
      }
    }
  }
  const def = s.def;
  px = Math.min(def.sizeX - r, Math.max(r, px));
  pz = Math.min(def.sizeZ - r, Math.max(r, pz));
  return { x: px, z: pz };
}
